import React, { useEffect, useState } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useNavigate } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../navbar/navbar';
import { LogoDropdown } from '../sidebar/components/LogoDropdown';
import { useTour } from '../../context/TourContext';

const tourSteps: Step[] = [
  // Welcome step (keep the full background overlay)
  {
    target: 'body',
    placement: 'center',
    content: (
      <div className="flex flex-col gap-6 p-6 min-w-[520px]">
        <div className="flex items-center gap-4">
          <div className="w-1.5 h-14 bg-gradient-to-b from-[#29DC5B] to-[#22c55e] rounded-full" />
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-semibold text-[#222222]">Welcome to Clodexa</h2>
            <p className="text-sm text-[#666666]">Your intelligent lead generation platform</p>
          </div>
        </div>
        <p className="text-[#666666] leading-relaxed text-base">
          Let's take a quick tour to help you understand how Clodexa can transform your lead generation process. We'll show you the key features that will help you find and engage with the most relevant leads.
        </p>
      </div>
    ),
    disableBeacon: true,
    styles: {
      options: {
        width: 520,
        zIndex: 1000,
      },
      spotlight: {
        backgroundColor: 'transparent',
        boxShadow: `
          0 0 0 9999px rgba(0, 0, 0, 0.95),
          0 0 0 9999px rgba(0, 0, 0, 0.95)
        `
      },
      overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(2px)'
      }
    }
  },
  // Navigation step (clean cutout without overlay)
  {
    target: '.sidebar-nav',
    content: (
      <div className="flex flex-col gap-5 p-4">
        <div className="flex items-center gap-3">
          <div className="w-1 h-10 bg-[#29DC5B] rounded-full" />
          <div className="flex flex-col gap-1">
            <h3 className="text-xl font-semibold text-[#222222]">Smart Navigation</h3>
            <p className="text-sm text-[#666666]">Your command center</p>
          </div>
        </div>
        <p className="text-[#666666] leading-relaxed">
          Access all key features through our intuitive sidebar. Navigate between dashboard pages with ease.
        </p>
      </div>
    ),
    placement: 'right',
    styles: {
      options: {
        width: 440,
      },
      spotlight: {
        backgroundColor: 'transparent',
        borderRadius: '12px',
        boxShadow: `
          0 0 0 9999px rgba(0, 0, 0, 0.65),
          0 0 0 3px #29DC5B
        `
      }
    }
  },
  // Accounts Section
  {
    target: '[data-menu-item="accounts"]',
    content: (
      <div className="flex flex-col gap-5 p-4">
        <div className="flex items-center gap-3">
          <div className="w-1 h-10 bg-[#29DC5B] rounded-full" />
          <div className="flex flex-col gap-1">
            <h3 className="text-xl font-semibold text-[#222222]">Accounts</h3>
            <p className="text-sm text-[#666666]">Discover Potential Customers</p>
          </div>
        </div>
        <p className="text-[#666666] leading-relaxed">
          This is your central hub for managing potential customer accounts. Here you can:
        </p>
        <div className="flex flex-col gap-2 bg-[#F9F9F9] p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-[#29DC5B] rounded-full" />
            <span className="text-sm text-[#444444]">View companies showing interest in your solutions</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-[#29DC5B] rounded-full" />
            <span className="text-sm text-[#444444]">Track engagement and activities</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-[#29DC5B] rounded-full" />
            <span className="text-sm text-[#444444]">Access detailed company insights</span>
          </div>
        </div>
      </div>
    ),
    placement: 'right',
    styles: {
      options: {
        width: 440,
      },
      spotlight: {
        backgroundColor: 'transparent',
        borderRadius: '12px',
        boxShadow: `
          0 0 0 9999px rgba(0, 0, 0, 0.65),
          0 0 0 3px #29DC5B
        `
      }
    }
  },
  // Intent Signals Section
  {
    target: '[data-menu-item="intent-signals"]',
    content: (
      <div className="flex flex-col gap-5 p-4">
        <div className="flex items-center gap-3">
          <div className="w-1 h-10 bg-[#29DC5B] rounded-full" />
          <div className="flex flex-col gap-1">
            <h3 className="text-xl font-semibold text-[#222222]">Intent Signals</h3>
            <p className="text-sm text-[#666666]">Your Lead Detection Engine</p>
          </div>
        </div>
        <p className="text-[#666666] leading-relaxed">
          Set up and manage your lead detection criteria. Intent signals help you identify companies actively looking for solutions like yours.
        </p>
        <div className="flex flex-col gap-2 bg-[#F9F9F9] p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-[#29DC5B] rounded-full" />
            <span className="text-sm text-[#444444]">Create custom intent triggers</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-[#29DC5B] rounded-full" />
            <span className="text-sm text-[#444444]">Track company behaviors</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-[#29DC5B] rounded-full" />
            <span className="text-sm text-[#444444]">Automate lead discovery</span>
          </div>
        </div>
      </div>
    ),
    placement: 'right',
    styles: {
      options: {
        width: 440,
      },
      spotlight: {
        backgroundColor: 'transparent',
        borderRadius: '12px',
        boxShadow: `
          0 0 0 9999px rgba(0, 0, 0, 0.65),
          0 0 0 3px #29DC5B
        `
      }
    }
  },
  // Notification Step (without background overlay)
  {
    target: '.notification-button',
    content: (
      <div className="flex flex-col gap-5 p-4">
        <div className="flex items-center gap-3">
          <div className="w-1 h-10 bg-[#29DC5B] rounded-full" />
          <div className="flex flex-col gap-1">
            <h3 className="text-xl font-semibold text-[#222222]">Smart Notifications</h3>
            <p className="text-sm text-[#666666]">Stay informed and engaged</p>
          </div>
        </div>
        <p className="text-[#666666] leading-relaxed">
          Get real-time updates about new leads, intent signals, and important activities. Our smart notification system ensures you never miss crucial opportunities.
        </p>
      </div>
    ),
    placement: 'bottom',
    styles: {
      options: {
        width: 440,
      },
      spotlight: {
        backgroundColor: 'transparent',
        borderRadius: '12px',
        boxShadow: `
          0 0 0 9999px rgba(0, 0, 0, 0.65),
          0 0 0 3px #29DC5B
        `
      }
    }
  },
  // Create Intent Signal Step (final step)
  {
    target: '[data-create-intent="button"]',
    content: (
      <div className="flex flex-col gap-5 p-4">
        <div className="flex items-center gap-3">
          <div className="w-1 h-10 bg-[#29DC5B] rounded-full" />
          <div className="flex flex-col gap-1">
            <h3 className="text-xl font-semibold text-[#222222]">Create Intent Signals</h3>
            <p className="text-sm text-[#666666]">Configure Your Lead Detection</p>
          </div>
        </div>
        <p className="text-[#666666] leading-relaxed">
          Start by creating your first intent signal. Define specific triggers that help identify potential customers:
        </p>
        <div className="flex flex-col gap-2 bg-[#F9F9F9] p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-[#29DC5B] rounded-full" />
            <span className="text-sm text-[#444444]">Set keywords and phrases to track</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-[#29DC5B] rounded-full" />
            <span className="text-sm text-[#444444]">Choose target industries and locations</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-[#29DC5B] rounded-full" />
            <span className="text-sm text-[#444444]">Define engagement criteria</span>
          </div>
        </div>
        <p className="text-sm text-[#666666] italic">
          Click here to create your first intent signal and start generating leads!
        </p>
      </div>
    ),
    placement: 'bottom',
    styles: {
      options: {
        width: 440,
      },
      spotlight: {
        backgroundColor: 'transparent',
        borderRadius: '12px',
        boxShadow: `
          0 0 0 9999px rgba(0, 0, 0, 0.65),
          0 0 0 3px #29DC5B
        `
      }
    }
  }
];

interface TourGuideProps {
  isRunning: boolean;
  onComplete: () => void;
}

const NewTourGuide: React.FC<TourGuideProps> = ({ isRunning, onComplete }) => {
  const { setIsTourRunning, setCurrentStep } = useTour();
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    setIsTourRunning(isRunning);
  }, [isRunning]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type, action, index } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status as 'finished' | 'skipped')) {
      setIsTourRunning(false);
      onComplete();
      return;
    }

    if (type === 'step:after') {
      const nextIndex = action === 'next' ? index + 1 : index - 1;
      setStepIndex(nextIndex);
      setCurrentStep(nextIndex);
    }
  };

  return (
    <AnimatePresence>
      {isRunning && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Navbar 
            isTourRunning={isRunning} 
            currentTourStep={stepIndex} 
          />
          <Joyride
            callback={handleJoyrideCallback}
            continuous
            hideCloseButton
            run={isRunning}
            scrollToFirstStep
            showProgress
            showSkipButton
            steps={tourSteps}
            stepIndex={stepIndex}
            locale={{
              last: 'Get Started',
              skip: 'Skip Tour',
              next: 'Next',
              back: 'Previous'
            }}
            styles={{
              options: {
                primaryColor: '#29DC5B',
                backgroundColor: '#FFFFFF',
                textColor: '#222222',
                zIndex: 10000,
                arrowColor: '#FFFFFF',
                overlayColor: 'transparent'
              },
              spotlight: {
                backgroundColor: 'transparent',
                borderRadius: '12px',
                boxShadow: '0 0 0 3px #29DC5B'
              },
              tooltipContainer: {
                textAlign: 'left',
                padding: '0',
                borderRadius: '16px',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
              },
              buttonNext: {
                backgroundColor: '#29DC5B',
                color: '#000000',
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: '500',
                borderRadius: '12px',
                transition: 'all 0.2s ease',
              },
              buttonBack: {
                color: '#666666',
                fontSize: '15px',
                fontWeight: '500',
                marginRight: '12px',
              },
              buttonSkip: {
                color: '#666666',
                fontSize: '15px',
                fontWeight: '500',
              },
              tooltip: {
                animation: 'fade-in 0.3s ease-out',
              },
            }}
            floaterProps={{
              styles: {
                floater: {
                  filter: 'drop-shadow(0 4px 24px rgba(0, 0, 0, 0.12))',
                },
              },
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewTourGuide; 